import React, { useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  AUTH_QUERY,
  DELETE_OBJECTS_MUTATION,
  GET_MY_OBJECTS_QUERY,
  GET_MY_OBJECT_GROUPS_QUERY,
  GET_MY_SITES_QUERY,
  UPDATE_OBJECTS_MUTATION,
} from '../../../gql';
import { useWorkspace } from '../../context/WorkspaceContext/useWorkspace';
import { isParentType, objectTypes, parentObjectTypes } from '../../enums';
import { FormattedMessage, useIntl } from 'react-intl';
import { useVocabulary } from '../../../i18n';
import { CondensedDangerZoneAction } from '../../design-system';

const inputStyles = { width: '100%', padding: 0 };
const labelStyles = { fontSize: 12, marginBottom: 0.25 };
const regularObjectTypeOptions = (objectTypes as unknown) as string[];
const parentObjectTypeOptions = (parentObjectTypes as unknown) as string[];

interface SiteOption {
  id: string;
  name: string;
}

interface GroupOption {
  id: string;
  name?: string | null;
  shared?: boolean | null;
  level?: number | null;
}

interface ObjectRecord {
  id: string;
  createdBy?: string | null;
  type?: string | null;
  siteId?: string | null;
  siteStatus?: 'none' | 'accessible' | 'restricted';
  groupId?: string | null;
  parentId?: string | null;
}

interface ParentOption {
  id: string;
  name?: string | null;
  type?: string | null;
}

type FormValues = {
  type: string | null;
  site: SiteOption | null;
  group: GroupOption | null;
  parent: ParentOption | null;
};

// Returns the shared value across all records for the given key, or null when
// the records disagree (or the set is empty).
function sharedValue<T extends string | null | undefined>(
  values: T[]
): T | null {
  if (values.length === 0) return null;
  const first = values[0] ?? null;
  for (let i = 1; i < values.length; i += 1) {
    if ((values[i] ?? null) !== first) return null;
  }
  return (first ?? null) as T | null;
}

interface BatchEditFormProps {
  selectedObjects: ObjectRecord[];
}

const BatchEditForm = ({ selectedObjects }: BatchEditFormProps) => {
  const intl = useIntl();
  const getVocabularyLabel = useVocabulary();
  const { clearBatchSelection, setActiveMapTool } = useWorkspace();

  const { data: authData } = useQuery(AUTH_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const viewerId: string | undefined = authData?.viewer?.user?.id;
  const allSelectedOwned =
    !!viewerId &&
    selectedObjects.every((object) => object.createdBy === viewerId);
  const selectedObjectsAreParents = selectedObjects.every((object) =>
    isParentType(object.type)
  );
  const typeOptions = selectedObjectsAreParents
    ? parentObjectTypeOptions
    : regularObjectTypeOptions;

  const { data: sitesData } = useQuery(GET_MY_SITES_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const sites: SiteOption[] = sitesData?.getMySites ?? [];

  const { data: groupsData } = useQuery(GET_MY_OBJECT_GROUPS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const allGroups: GroupOption[] = groupsData?.getMyObjectGroups ?? [];
  const groups: GroupOption[] = useMemo(
    () => allGroups.filter((g) => !g.shared || (g.level ?? -1) >= 2),
    [allGroups]
  );

  const { data: objectsData } = useQuery(GET_MY_OBJECTS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const selectedIds = useMemo(() => new Set(selectedObjects.map((o) => o.id)), [
    selectedObjects,
  ]);
  const parentOptions: ParentOption[] = useMemo(() => {
    const all: ParentOption[] = objectsData?.getMyObjects ?? [];
    return all
      .filter((o) => isParentType(o.type) && !selectedIds.has(o.id))
      .map((o) => ({ id: o.id, name: o.name, type: o.type }));
  }, [objectsData, selectedIds]);

  const sharedType = useMemo(
    () => sharedValue(selectedObjects.map((o) => o.type ?? null)),
    [selectedObjects]
  );
  const sharedSiteId = useMemo(
    () => sharedValue(selectedObjects.map((o) => o.siteId ?? null)),
    [selectedObjects]
  );
  const sharedSiteStatus = useMemo(
    () => sharedValue(selectedObjects.map((o) => o.siteStatus ?? null)),
    [selectedObjects]
  );
  const sharedGroupId = useMemo(
    () => sharedValue(selectedObjects.map((o) => o.groupId ?? null)),
    [selectedObjects]
  );
  const sharedParentId = useMemo(
    () => sharedValue(selectedObjects.map((o) => o.parentId ?? null)),
    [selectedObjects]
  );

  const defaultValues: FormValues = useMemo(() => {
    const site = sharedSiteId
      ? sites.find((s) => s.id === sharedSiteId) ?? null
      : null;
    const group = sharedGroupId
      ? allGroups.find((g) => g.id === sharedGroupId) ??
        ({ id: sharedGroupId, name: sharedGroupId } as GroupOption)
      : null;
    const parent = sharedParentId
      ? parentOptions.find((p) => p.id === sharedParentId) ??
        ({ id: sharedParentId, name: sharedParentId } as ParentOption)
      : null;
    return {
      type: sharedType ?? null,
      site,
      group,
      parent,
    };
  }, [
    sharedType,
    sharedSiteId,
    sharedGroupId,
    sharedParentId,
    sites,
    allGroups,
    parentOptions,
  ]);

  const typePlaceholder =
    sharedType === null && selectedObjects.length > 1
      ? intl.formatMessage({
          id: 'map.objects.multipleValues',
          defaultMessage: 'Multiple values',
        })
      : intl.formatMessage({
          id: 'map.objects.selectType',
          defaultMessage: 'Select type',
        });
  const sitePlaceholder =
    sharedSiteStatus === 'restricted'
      ? intl.formatMessage({
          id: 'map.objects.siteRestricted',
          defaultMessage: 'Linked site not shared with you',
        })
      : sharedSiteId === null && selectedObjects.length > 1
      ? intl.formatMessage({
          id: 'map.objects.multipleValues',
          defaultMessage: 'Multiple values',
        })
      : intl.formatMessage({ id: 'common.none', defaultMessage: 'None' });
  const groupPlaceholder =
    sharedGroupId === null && selectedObjects.length > 1
      ? intl.formatMessage({
          id: 'map.objects.multipleValues',
          defaultMessage: 'Multiple values',
        })
      : intl.formatMessage({ id: 'common.none', defaultMessage: 'None' });
  const parentPlaceholder =
    sharedParentId === null && selectedObjects.length > 1
      ? intl.formatMessage({
          id: 'map.objects.multipleValues',
          defaultMessage: 'Multiple values',
        })
      : intl.formatMessage({ id: 'common.none', defaultMessage: 'None' });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [updateObjects, { error: updateError }] = useMutation(
    UPDATE_OBJECTS_MUTATION,
    {
      refetchQueries: [{ query: GET_MY_OBJECTS_QUERY }],
    }
  );
  const [
    deleteObjects,
    { loading: deleting, error: deleteError },
  ] = useMutation(DELETE_OBJECTS_MUTATION, {
    refetchQueries: [{ query: GET_MY_OBJECTS_QUERY }],
  });

  const closeAndReset = () => {
    clearBatchSelection();
    setActiveMapTool(null);
  };

  const handleDelete = async () => {
    if (selectedObjects.length === 0) return;
    if (
      !window.confirm(
        intl.formatMessage(
          {
            id: 'map.objects.confirmBatchDelete',
            defaultMessage:
              'Delete {count, plural, one {# object} other {# objects}}? This cannot be undone.',
          },
          { count: selectedObjects.length }
        )
      )
    ) {
      return;
    }
    await deleteObjects({
      variables: { ids: selectedObjects.map((o) => o.id) },
    });
    closeAndReset();
  };

  const submit: SubmitHandler<FormValues> = async (values) => {
    const input: Record<string, any> = {};
    if (values.type) input.type = values.type;
    if (allSelectedOwned && values.site) {
      input.siteId = values.site.id;
    }
    if (values.group) input.groupId = values.group.id;
    if (values.parent) input.parentId = values.parent.id;

    if (Object.keys(input).length === 0) {
      closeAndReset();
      return;
    }

    const result = await updateObjects({
      variables: {
        ids: selectedObjects.map((o) => o.id),
        object: input,
      },
    });
    const payload = result.data?.updateObjects;
    if (payload?.__typename === 'ValidationError') {
      throw new Error(
        payload.errors?.map((e: { error: string }) => e.error).join(', ') ||
          intl.formatMessage({
            id: 'map.objects.saveBatchError',
            defaultMessage: 'Failed to save objects',
          })
      );
    }

    closeAndReset();
  };

  const onSubmit = handleSubmit(submit);

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mt: 1,
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">
          <FormattedMessage
            id="map.objects.batchEdit"
            defaultMessage="Batch edit"
          />
        </Typography>

        <Typography variant="caption" color="text.secondary">
          <FormattedMessage
            id="map.objects.batchEditHelp"
            defaultMessage="Leave a field empty to keep each object's current value."
          />
        </Typography>

        <Stack direction="column">
          <InputLabel sx={labelStyles} htmlFor="batch-object-type">
            <FormattedMessage id="form.type" defaultMessage="Type" />
          </InputLabel>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <Autocomplete
                disablePortal
                id="batch-object-type"
                size="small"
                options={typeOptions}
                value={value}
                onChange={(_, next) => onChange(next)}
                getOptionLabel={(o) => getVocabularyLabel('objectType', o)}
                renderInput={(params) => (
                  <TextField
                    inputRef={ref}
                    {...params}
                    placeholder={typePlaceholder}
                    sx={inputStyles}
                  />
                )}
                sx={inputStyles}
              />
            )}
          />
        </Stack>

        <Stack direction="column">
          <InputLabel sx={labelStyles} htmlFor="batch-object-site">
            <FormattedMessage id="form.site" defaultMessage="Site" />
          </InputLabel>
          <Controller
            name="site"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <Autocomplete
                disablePortal
                id="batch-object-site"
                size="small"
                disabled={!allSelectedOwned}
                options={sites}
                value={value}
                onChange={(_, next) => onChange(next)}
                getOptionLabel={(o) => o?.name ?? ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    inputRef={ref}
                    {...params}
                    placeholder={sitePlaceholder}
                    sx={inputStyles}
                  />
                )}
                sx={inputStyles}
              />
            )}
          />
          {sharedSiteStatus === 'restricted' && (
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage
                id="map.objects.siteRestricted"
                defaultMessage="Linked site not shared with you"
              />
            </Typography>
          )}
        </Stack>

        <Stack direction="column">
          <InputLabel sx={labelStyles} htmlFor="batch-object-group">
            <FormattedMessage id="form.group" defaultMessage="Group" />
          </InputLabel>
          <Controller
            name="group"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <Autocomplete
                disablePortal
                id="batch-object-group"
                size="small"
                options={groups}
                value={value}
                onChange={(_, next) => onChange(next)}
                getOptionLabel={(o) => o?.name ?? ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    inputRef={ref}
                    {...params}
                    placeholder={groupPlaceholder}
                    sx={inputStyles}
                  />
                )}
                sx={inputStyles}
              />
            )}
          />
        </Stack>

        <Stack direction="column">
          <InputLabel sx={labelStyles} htmlFor="batch-object-parent">
            <FormattedMessage id="form.parent" defaultMessage="Parent" />
          </InputLabel>
          <Controller
            name="parent"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <Autocomplete
                disablePortal
                id="batch-object-parent"
                size="small"
                options={parentOptions}
                value={value}
                onChange={(_, next) => onChange(next)}
                getOptionLabel={(o) => o?.name ?? ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    inputRef={ref}
                    {...params}
                    placeholder={parentPlaceholder}
                    sx={inputStyles}
                  />
                )}
                sx={inputStyles}
              />
            )}
          />
        </Stack>

        {(updateError || deleteError) && (
          <Alert severity="error" variant="outlined">
            {updateError?.message || deleteError?.message}
          </Alert>
        )}

        <CondensedDangerZoneAction
          actionIcon={<DeleteIcon fontSize="small" />}
          actionLabel={
            <FormattedMessage
              id="map.objects.deleteCount"
              defaultMessage="Delete {count, plural, one {# object} other {# objects}}"
              values={{ count: selectedObjects.length }}
            />
          }
          background="sunken"
          disabled={deleting || selectedObjects.length === 0}
          label={
            <FormattedMessage
              id="form.dangerZone"
              defaultMessage="Danger zone"
            />
          }
          onAction={handleDelete}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={closeAndReset} disabled={isSubmitting}>
            <FormattedMessage id="action.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            size="small"
            type="submit"
            variant="contained"
            disabled={isSubmitting || selectedObjects.length === 0}
          >
            <FormattedMessage id="action.save" defaultMessage="Save" />
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export { BatchEditForm };
